import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BringToFront, SendToBack } from 'lucide-react'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuItemTrailing,
  ContextMenuShortcut,
} from '@/src/renderer/components/ui/context-menu'
import { useShortcutText } from '@/src/renderer/hooks/useShortcutText'
import type { ZOrderDirection } from './utils/canvasZOrder'
import './CanvasElementMenu.scss'

/** Was das Menü gerade anbieten kann — `false` heißt: Die Zeile steht grau da. */
export interface CanvasElementMenuAvailability {
  front: boolean
  back: boolean
}

export interface CanvasElementMenuProps {
  /** Das Pinnwand-Element, auf dem der Rechtsklick liegt. */
  children: React.ReactNode
  /**
   * Vor dem Öffnen gefragt, mit dem Rechtsklick selbst. `false` hält das Menü
   * zu — im Bearbeiten-Modus gehört der Rechtsklick dem Text. Hier wählt die
   * Pinnwand das Element auch aus, falls es noch nicht zur Auswahl gehört.
   */
  onBeforeOpen?: (event: React.MouseEvent) => boolean
  /** Beim Öffnen gefragt: Was ginge für die Auswahl? */
  getAvailability: () => CanvasElementMenuAvailability
  onSelect: (direction: ZOrderDirection) => void
  /** Wohin der Fokus nach dem Schließen geht — sonst läge er nirgends, und ⌘Z erreichte die Pinnwand nicht. */
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * Das Menü am Rechtsklick auf ein **Pinnwand-Element** (CONTEXT.md, „Vorn und
 * hinten"): ganz nach vorn, ganz nach hinten. Bei einer Mehrfachauswahl gilt
 * es allen Ausgewählten.
 *
 * Der Auslöser umschließt das Element ohne eigenen Kasten (`display:
 * contents`) — er muss INNERHALB von React Flows Node sitzen: Dessen Fläche
 * hält jedes `contextmenu` auf, weil Rechtsziehen dort verschiebt, und ein
 * Menü um die ganze Fläche käme deshalb nie zum Zug.
 *
 * @example
 * <CanvasElementMenu getAvailability={…} onSelect={dir => move(dir)}>
 *   <NoteNode {...props} />
 * </CanvasElementMenu>
 */
export const CanvasElementMenu: React.FC<CanvasElementMenuProps> = ({
  children, onBeforeOpen, getAvailability, onSelect, onCloseAutoFocus,
}) => {
  const { t } = useTranslation()
  const shortcutText = useShortcutText()
  const [availability, setAvailability] = useState<CanvasElementMenuAvailability>({ front: true, back: true })

  return (
    <ContextMenu onOpenChange={open => { if (open) setAvailability(getAvailability()) }}>
      <ContextMenuTrigger
        className="canvas-element-menu__trigger"
        onContextMenu={event => { if (onBeforeOpen && !onBeforeOpen(event)) event.preventDefault() }}
      >
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent onCloseAutoFocus={onCloseAutoFocus}>
        <ContextMenuItem disabled={!availability.front} onSelect={() => onSelect('front')}>
          <span className="context-menu-content__item-icon"><BringToFront size={16} /></span>
          {t('projectCanvas.menu.bringToFront')}
          <ContextMenuItemTrailing>
            <ContextMenuShortcut>{shortcutText('⇧⌘↑')}</ContextMenuShortcut>
          </ContextMenuItemTrailing>
        </ContextMenuItem>
        <ContextMenuItem disabled={!availability.back} onSelect={() => onSelect('back')}>
          <span className="context-menu-content__item-icon"><SendToBack size={16} /></span>
          {t('projectCanvas.menu.sendToBack')}
          <ContextMenuItemTrailing>
            <ContextMenuShortcut>{shortcutText('⇧⌘↓')}</ContextMenuShortcut>
          </ContextMenuItemTrailing>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
